/**
 * Remotion Text Overlay Component
 *
 * Renders text with styling - replaces Fabric.js text rendering.
 * Uses pure CSS for styling, making it identical in preview and export.
 */

import React from 'react';
import { TextOverlayData } from '../types';

interface TextOverlayProps {
  overlay: TextOverlayData;
  containerWidth: number;
  containerHeight: number;
}

export const TextOverlay: React.FC<TextOverlayProps> = ({
  overlay,
  containerWidth,
  containerHeight,
}) => {
  const { text, style, position, width, height, rotation = 0, zIndex = 0 } = overlay;

  // Convert percentage dimensions to pixels
  const left = (position.x / 100) * containerWidth;
  const top = (position.y / 100) * containerHeight;
  const widthPx = (width / 100) * containerWidth;
  const heightPx = height ? (height / 100) * containerHeight : undefined;

  // Build padding string
  const padding = style.padding
    ? `${style.padding.top ?? 8}px ${style.padding.right ?? 12}px ${style.padding.bottom ?? 8}px ${style.padding.left ?? 12}px`
    : '8px 12px';

  // Background with opacity
  const bgOpacity = (style.opacity ?? 100) / 100;
  const bgColor = style.backgroundColor || 'transparent';
  const backgroundColor =
    bgOpacity < 1 && bgColor !== 'transparent'
      ? hexToRgba(bgColor, bgOpacity)
      : bgColor;

  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width: widthPx,
        height: heightPx,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: 'top left',
        zIndex,
        // Text styling
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        color: style.color,
        textAlign: style.textAlign,
        lineHeight: style.lineHeight ?? 1.4,
        // Background
        backgroundColor,
        borderRadius: style.borderRadius ?? 0,
        padding,
        // Ensure text wraps properly
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        boxSizing: 'border-box',
      }}
    >
      {text}
    </div>
  );
};

// Helper to convert hex + opacity to rgba
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export default TextOverlay;
